<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  examboards: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Exam Boards & Specifications'
  },
  description: {
    type: String,
    default: 'Select the exam boards you have experience teaching'
  },
  showShadow: {
    type: Boolean,
    default: true
  },
  viewMode: {
    type: String,
    default: 'detailed', // 'detailed' or 'simple'
    validator: (value) => ['detailed', 'simple'].includes(value)
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const toggleExamboard = (id) => {
  const selected = [...props.modelValue]
  const index = selected.indexOf(id)

  if (index === -1) {
    selected.push(id)
  } else {
    selected.splice(index, 1)
  }

  emit('update:modelValue', selected)

  // Clear error when user makes a selection
  if (localErrors.value.selected_examboards) {
    delete localErrors.value.selected_examboards
  }
}

const isSelected = (id) => props.modelValue.includes(id)

const getInitials = (name) => {
  return name.substring(0, 3).toUpperCase()
}

const validate = () => {
  const errors = {}

  if (props.modelValue.length === 0) {
    errors.selected_examboards = 'Please select at least one exam board'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div :class="['bg-white rounded-2xl p-10', showShadow ? 'shadow-lg' : '']">
    <div class="mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600">{{ description }}</p>

      <!-- Error Message -->
      <div v-if="displayErrors.selected_examboards" class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ displayErrors.selected_examboards }}</p>
            <p class="text-red-700 text-sm mt-1">Choose at least one exam board from the options below.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Detailed View -->
    <div v-if="viewMode === 'detailed'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <button
        v-for="examboard in examboards"
        :key="examboard.id"
        type="button"
        @click="toggleExamboard(examboard.id)"
        class="bg-white border-2 rounded-xl p-6 text-left hover:shadow-md transition-all"
        :class="isSelected(examboard.id) ? 'shadow-md' : 'border-gray-200'"
        :style="{
          borderColor: isSelected(examboard.id) ? examboard.color : '',
          backgroundColor: isSelected(examboard.id) ? `${examboard.color}10` : ''
        }"
      >
        <div class="flex items-center justify-between mb-3">
          <div
            class="w-12 h-12 rounded-xl flex items-center justify-center bg-brand-soft"
          >
            <span class="text-gray-600 font-bold text-sm">{{ getInitials(examboard.name) }}</span>
          </div>
          <span
            v-if="isSelected(examboard.id)"
            class="material-symbols-outlined text-xl text-brand"
          >
            check_circle
          </span>
          <span
            v-else
            class="material-symbols-outlined text-xl text-gray-300"
          >
            add_circle
          </span>
        </div>
        <p class="text-md font-semibold text-gray-900 mb-1">{{ examboard.name }}</p>
        <p class="text-sm text-gray-500">{{ examboard.description }}</p>
      </button>
    </div>

    <!-- Simple View -->
    <div v-else class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <button
        v-for="examboard in examboards"
        :key="examboard.id"
        type="button"
        @click="toggleExamboard(examboard.id)"
        class="relative group px-4 py-3 rounded-lg border-2 transition-all hover:shadow-sm"
        :class="isSelected(examboard.id) ? '' : 'border-gray-200 bg-white hover:border-gray-300'"
        :style="{
          borderColor: isSelected(examboard.id) ? examboard.color : '',
          backgroundColor: isSelected(examboard.id) ? `${examboard.color}08` : ''
        }"
      >
        <span class="text-sm font-semibold text-gray-900 text-center block">{{ examboard.name }}</span>

        <!-- Triangle indicator for selected examboards -->
        <div
          v-if="isSelected(examboard.id)"
          class="absolute top-0 right-0 w-0 h-0"
          :style="{
            borderTop: `20px solid ${examboard.color}`,
            borderLeft: '20px solid transparent'
          }"
        ></div>
      </button>
    </div>
  </div>
</template>
